import { parseDateFromISO, formatDateForDb, formatDateTimeForDb } from '@applicator/shared'

import { DateFieldConfig, JobDatesData, DateMode, UserRules } from './types'

// Determine if field uses TIMESTAMPTZ (date and time) or DATE (date only)
export function isTimestampField(fieldKey: string): boolean {
  return fieldKey === 'date_applied' || fieldKey === 'deadline' || fieldKey === 'date_saved'
}

const CHIP_PICKER_VIEWPORT_MARGIN = 8

/** Horizontal placement for job date chip pickers (wide time picker aligns to chip edge). */
export function resolveChipPickerLeft(
  rect: DOMRect,
  pickerWidth: number,
  viewportWidth: number,
  isTimestamp: boolean,
): number {
  const margin = CHIP_PICKER_VIEWPORT_MARGIN
  const chipRight = rect.left + rect.width

  if (isTimestamp) {
    const rightAlignedLeft = chipRight - pickerWidth
    if (chipRight <= viewportWidth - margin && rightAlignedLeft >= margin) {
      return rightAlignedLeft
    }
    let left = rect.left
    if (left + pickerWidth > viewportWidth - margin) {
      left = Math.max(margin, viewportWidth - pickerWidth - margin)
    }
    return left
  }

  let left = rect.left
  if (left + pickerWidth > viewportWidth - margin) {
    left = Math.max(margin, viewportWidth - pickerWidth - margin)
  }
  if (left < margin) left = margin
  return left
}

// Compute effective date for a field
export function computeEffectiveDate(
  field: DateFieldConfig,
  jobDates: JobDatesData,
  userRules: UserRules
): Date | null {
  const modeKey = `${field.key}_mode` as keyof JobDatesData
  const manualKey = `${field.key}_manual` as keyof JobDatesData
  const mode = jobDates[modeKey] as DateMode | undefined

  if (mode === 'manual') {
    const manualValue = jobDates[manualKey] as string | null | undefined
    return parseDateFromISO(manualValue)
  }

  if (mode === 'cleared') {
    return null
  }

  // Auto mode: compute from rules
  return field.getAutoDate(jobDates, userRules)
}

// Map field keys to database column names
export function getDbColumnForField(fieldKey: string): string {
  const fieldToDbColumn: Record<string, string> = {
    'date_saved': 'saved_at',
    'date_applied': 'applied_at',
    'deadline': 'deadline_at',
    'follow_up': 'follow_up_at',
  }
  return fieldToDbColumn[fieldKey] || fieldKey
}

// Format date value for database
export function formatDateForDatabase(date: Date | null, fieldKey: string): string | null {
  if (!date) return null
  return isTimestampField(fieldKey)
    ? formatDateTimeForDb(date)
    : formatDateForDb(date)
}
